import React, { useEffect, useState } from 'react';
import { ArrowRight } from 'lucide-react';
import { BBA_TEAM_MEMBERS } from '../../StartMeetTheTeam';
import {
  welcomeGlass,
  welcomeGlassInteractive,
  welcomeEyebrow,
  welcomeBodyText,
  welcomeDepartmentAccent,
} from '../welcomeTokens';

const initials = (name) =>
  name
    .split(' ')
    .map((part) => part[0])
    .join('');

const Avatar = ({ member, className }) =>
  member.avatarUrl ? (
    <img
      src={member.avatarUrl}
      alt={member.name}
      className={`${className} object-cover`}
    />
  ) : (
    <span
      className={`${className} flex items-center justify-center bg-gradient-to-br from-red-500 to-red-700 text-2xl font-bold text-white`}
    >
      {initials(member.name)}
    </span>
  );

/**
 * "Our Team" popup for /welcome/JTBootcamp — roster imported from the /start
 * subpage (single source of truth), restyled into the welcome page's dark glass
 * language. Selecting a member swaps the grid for a full detail view, which
 * reads better inside a modal than the /start page's reflowing grid.
 *
 * The way back is an arrow in the popup header (via `onBackChange`), not a
 * button in the scrolling body — that one scrolled out of reach on phones.
 */
const WelcomeTeamPanel = ({ onBackChange }) => {
  const [selectedId, setSelectedId] = useState(null);
  const selected = BBA_TEAM_MEMBERS.find((m) => m.id === selectedId) || null;

  useEffect(() => {
    if (!onBackChange) return undefined;
    onBackChange(
      selectedId ? { label: 'Back to the team', onBack: () => setSelectedId(null) } : null
    );
    return () => onBackChange(null);
  }, [selectedId, onBackChange]);

  if (selected) {
    const accent = welcomeDepartmentAccent(selected.department);
    return (
      <div className="mx-auto w-full max-w-5xl px-4 py-8 sm:px-6 sm:py-10">
        <article className={`relative overflow-hidden rounded-2xl p-6 sm:p-8 ${welcomeGlass}`}>
          <span
            className="pointer-events-none absolute inset-x-0 top-0 h-px"
            style={{ background: `linear-gradient(90deg, transparent, ${accent}, transparent)` }}
            aria-hidden
          />
          <span
            className="pointer-events-none absolute -right-24 -top-24 h-56 w-56 rounded-full opacity-20 blur-3xl"
            style={{ background: accent }}
            aria-hidden
          />
          <div className="relative flex flex-col gap-8 md:flex-row">
            <div className="flex flex-col items-center md:w-56 md:shrink-0">
              <Avatar
                member={selected}
                className="h-36 w-36 rounded-full ring-2 ring-white/10"
              />
              <h3 className="mt-5 text-center text-lg font-bold text-white">
                {selected.name}
              </h3>
              <p className="mt-1 text-center text-sm text-zinc-400">{selected.title}</p>
              <span
                className="mt-4 inline-flex items-center rounded-full border px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.12em]"
                style={{
                  color: accent,
                  borderColor: `${accent}55`,
                  backgroundColor: `${accent}1f`,
                }}
              >
                {selected.department}
              </span>
            </div>

            <div className="min-w-0 flex-1 space-y-6">
              <div>
                <h4 className={welcomeEyebrow}>Background</h4>
                <p className={`mt-2 ${welcomeBodyText}`}>{selected.background}</p>
              </div>
              {selected.expertise?.length > 0 && (
                <div>
                  <h4 className={welcomeEyebrow}>Expertise</h4>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {selected.expertise.map((skill) => (
                      <span
                        key={skill}
                        className="rounded-lg border border-white/10 bg-white/[0.05] px-2.5 py-1 text-xs text-zinc-300"
                      >
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </article>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-5xl px-4 py-8 sm:px-6 sm:py-10">
      <p className={`${welcomeEyebrow} text-center`}>Builders Breakthrough Advisors</p>
      <h2 className="mt-2 text-center text-2xl font-bold tracking-tight text-white sm:text-3xl">
        The advisors behind your program
      </h2>
      <p className="mx-auto mt-3 max-w-xl text-center text-sm text-zinc-500">
        Six specialists, one per department — pick anyone to read their story.
      </p>

      <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {BBA_TEAM_MEMBERS.map((member) => {
          const accent = welcomeDepartmentAccent(member.department);
          return (
            <button
              key={member.id}
              type="button"
              onClick={() => setSelectedId(member.id)}
              className={`group relative flex flex-col items-center overflow-hidden rounded-2xl p-6 text-center focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2 focus-visible:ring-offset-zinc-900 ${welcomeGlassInteractive}`}
            >
              <span
                className="pointer-events-none absolute inset-x-0 top-0 h-px"
                style={{ background: `linear-gradient(90deg, transparent, ${accent}, transparent)` }}
                aria-hidden
              />
              <span
                className="pointer-events-none absolute -right-16 -top-16 h-32 w-32 rounded-full opacity-0 blur-3xl transition-opacity duration-300 group-hover:opacity-30"
                style={{ background: accent }}
                aria-hidden
              />
              <Avatar
                member={member}
                className="relative h-28 w-28 rounded-full ring-2 ring-white/10 transition-transform duration-300 group-hover:scale-[1.04]"
              />
              <h3 className="relative mt-4 text-base font-bold text-white">{member.name}</h3>
              <p className="relative mt-0.5 text-xs text-zinc-500">{member.title}</p>
              <span
                className="relative mt-3 inline-flex items-center rounded-full border px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.12em]"
                style={{
                  color: accent,
                  borderColor: `${accent}55`,
                  backgroundColor: `${accent}1f`,
                }}
              >
                {member.department}
              </span>
              <span className="relative mt-4 inline-flex items-center gap-1.5 text-xs font-semibold text-zinc-400 transition-colors group-hover:text-red-400">
                Read their story
                <ArrowRight
                  className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5"
                  aria-hidden
                />
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default WelcomeTeamPanel;
